import { Variable, VariableSummary } from '@mtna/pojo-consumer-ui';

import { MeasureOptionType } from '../../measure-select/measure-option.model';
import { MeasureSelection } from '../../measure-select/measure-selection.model';
import { RdsBaseParameters, serializeBaseUrlParam } from './parameters.model';
import { serializeArrayToUrlParams } from './serialization-functions.model';

/**
 * Various parameters for Tabulation REST calls
 */
export interface TabulationParameters extends RdsBaseParameters {
  dims: Array<string>;
  measure?: Array<string>;
  totals?: boolean;
  weights?: Array<string>;
  where?: string;
}

/**
 * Serialize a single measure into a url param as `name:func(variableId)`
 * @param measure the measure to serialize
 * @return the string representation of the measure to use as URL parameters
 */
export function serializeMeasureToUrlParam<V extends Variable | VariableSummary = Variable>(measure: MeasureSelection<V>): string {
  if (!measure) {
    return '';
  }
  const nameOfComputedVariabe = !!measure.variable ? measure.variable.name : measure.measureType;
  const variableBeingMeasured = !!measure.variable ? measure.variable.id : '*';
  return `${nameOfComputedVariabe}:${measure.measureType}(${variableBeingMeasured})`;
}

/**
 * Deserialize a single measure from a url param as `name:func(variableId)` to a `MeasureSelection`
 * @param value string to deserialize back into a measure
 * @return the `MeasureSelection` object deserialized from the given string
 */
export function deserializeMeasureFromUrlParam<V extends Variable | VariableSummary = Variable>(
  value: string,
  variables: Array<V>
): MeasureSelection<V> {
  if (!value) {
    return null;
  }
  const splitOnColon = value.split(':');
  if (splitOnColon.length !== 2) {
    return null;
  }

  const func = splitOnColon[1];
  const indexOfOpenParenthesis = func.indexOf('(');
  const measureType = func.slice(0, indexOfOpenParenthesis).toUpperCase() as MeasureOptionType;
  const variableIdBeingMeasured = func.slice(indexOfOpenParenthesis + 1, func.length - 1);
  const variable: V | null = variableIdBeingMeasured === '*' ? null : variables.find(v => v.id === variableIdBeingMeasured);
  return new MeasureSelection<V>(measureType, variable);
}

/**
 * Given TabulationParameters, serialize them to be used as
 * URL parameters
 *
 * @param  options options to serialize
 * @returns serialized string as a urlParameter
 */
export function serializeTabulationOptionsUrlParams(options: TabulationParameters): string {
  if (!options) {
    return '';
  }
  let path = '';
  for (const prop in options) {
    if (options.hasOwnProperty(prop)) {
      const value = options[prop];
      switch (prop) {
        case 'measure':
        case 'dims':
        case 'weights':
          path += serializeArrayToUrlParams(prop, value);
          break;
        default:
          path += serializeBaseUrlParam(prop, value);
      }
    }
  }

  // Remove trailing ampersand
  if (path[path.length - 1] === '&') {
    path = path.substr(0, path.length - 1);
  }

  return path;
}
